import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Megaphone, ExternalLink, Check, Play, Flag, Ban, Upload } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getAdminMarketingRequests, decideMarketingRequest, uploadAsset } from "@/lib/afmApi";
import { StatusBadge } from "@/components/StatusBadge";
import { Loading, SectionTitle } from "@/components/common";
import { fmtDate } from "@/lib/format";

const ADMIN_ROLES = ["admin", "distribution_admin", "superadmin", "staff"];
const MK_STATUS = {
  pending_verification: { label: "Pago en verificación", dot: "#F59E0B", cls: "border-amber-300 bg-amber-50 text-amber-800" },
  verified: { label: "Pago verificado", dot: "#3B82F6", cls: "border-blue-300 bg-blue-50 text-blue-700" },
  running: { label: "En curso", dot: "#8B5CF6", cls: "border-violet-300 bg-violet-50 text-violet-700" },
  completed: { label: "Completada", dot: "#10B981", cls: "border-emerald-300 bg-emerald-50 text-emerald-700" },
  rejected: { label: "Rechazada", dot: "#EF4444", cls: "border-red-300 bg-red-50 text-red-700" },
};
const FILTERS = [["", "Todas"], ["pending_verification", "En verificación"], ["verified", "Verificadas"], ["running", "En curso"], ["completed", "Completadas"], ["rejected", "Rechazadas"]];
const PAY_LABEL = { pago_movil: "Pago Móvil", paypal: "PayPal", binance: "Binance Pay" };

export default function AdminMarketing() {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role) || user?.super_admin;
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("loading");
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState("");
  const [acting, setActing] = useState(null);
  const [note, setNote] = useState("");
  const [reportUrl, setReportUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    if (!isAdmin) { setStatus("denied"); return; }
    setStatus("loading");
    getAdminMarketingRequests(user.id, filter)
      .then((d) => { setItems(Array.isArray(d) ? d : []); setStatus("ok"); })
      .catch((e) => setStatus(e?.response?.status === 403 ? "denied" : "error"));
  }, [user.id, filter, isAdmin]);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, newStatus, extra = {}) => {
    setBusy(id);
    try { await decideMarketingRequest(id, { viewer_id: user.id, status: newStatus, ...extra }); toast.success("Campaña actualizada"); setActing(null); setNote(""); setReportUrl(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo actualizar"); } finally { setBusy(""); }
  };
  const uploadReport = async (file) => {
    if (!file) return;
    setUploading(true);
    try { const { url } = await uploadAsset(user.id, file); setReportUrl(url); toast.success("Archivo subido"); }
    catch { toast.error("No se pudo subir"); } finally { setUploading(false); }
  };

  if (status === "loading") return <Loading />;
  if (status === "denied") return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white py-20 text-center" data-testid="mkt-admin-denied">
      <div className="mb-4 rounded-full border border-amber-300 bg-amber-50 p-4 text-amber-700"><ShieldAlert className="h-7 w-7" /></div>
      <h3 className="font-serif text-xl font-bold text-black">Solo administradores</h3>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="admin-marketing-page">
      <SectionTitle>Campañas de Marketing</SectionTitle>
      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([k, label]) => (
          <button key={k || "all"} data-testid={`mkt-filter-${k || "all"}`} onClick={() => setFilter(k)} className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${filter === k ? "border-black bg-black text-white" : "border-slate-300 bg-white text-slate-600 hover:border-black"}`}>{label}</button>
        ))}
      </div>
      {!items.length ? (
        <p className="rounded-xl border border-dashed border-slate-300 bg-white px-5 py-16 text-center text-sm text-slate-400">Sin campañas en esta categoría.</p>
      ) : (
        <div className="space-y-4">
          {items.map((r) => (
            <div key={r.id} data-testid={`mkt-item-${r.id}`} className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2"><h3 className="font-serif text-lg font-bold text-black">{r.service}</h3><StatusBadge map={MK_STATUS} status={r.status} /></div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                    {r.release_title && <span>Tema: {r.release_title}</span>}
                    <span>· {r.user_name || r.user_email || r.user_id}</span>
                    <span>· {fmtDate(r.created_at)}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-serif text-base font-bold text-[#AC3333]">{r.budget_tier}</div>
                  {r.applied_credit_usd > 0 && <div className="text-[11px] font-semibold text-emerald-600">Bono -${r.applied_credit_usd} · neto ${r.net_due_usd}</div>}
                  <div className="text-[11px] text-slate-500">{PAY_LABEL[r.payment_method] || r.payment_method} · Ref <span className="font-mono">{r.payment_reference}</span></div>
                </div>
              </div>
              {r.payment_proof_url && <a href={r.payment_proof_url} target="_blank" rel="noreferrer" data-testid={`mkt-proof-${r.id}`} className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#AC3333] hover:underline"><ExternalLink className="h-3.5 w-3.5" /> Ver comprobante</a>}
              {(r.objective || r.audience || r.links || r.notes || r.goal) && (
                <div className="mt-3 space-y-2 rounded-lg bg-slate-50 p-3 text-sm text-slate-700" data-testid={`mkt-brief-${r.id}`}>
                  {(r.objective || r.goal) && <p><span className="font-semibold text-slate-900">Objetivo:</span> {r.objective || r.goal}</p>}
                  {r.audience && <p><span className="font-semibold text-slate-900">Público:</span> {r.audience}</p>}
                  {r.links && <p className="break-words"><span className="font-semibold text-slate-900">Enlaces:</span> {r.links}</p>}
                  {r.notes && <p><span className="font-semibold text-slate-900">Notas:</span> {r.notes}</p>}
                </div>
              )}
              {r.admin_note && <p className="mt-2 text-xs text-slate-500">Respuesta: {r.admin_note}</p>}
              {r.report_url && <a href={r.report_url} target="_blank" rel="noreferrer" data-testid={`mkt-report-${r.id}`} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 hover:underline"><ExternalLink className="h-3.5 w-3.5" /> Reporte / resultados</a>}
              {acting?.id === r.id ? (
                <div className="mt-4 space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid={`mkt-acting-${r.id}`}>
                  {acting.status === "completed" && (
                    <>
                      <label data-testid={`mkt-upload-${r.id}`} className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-3 text-sm font-semibold text-slate-600 hover:border-black"><Upload className="h-4 w-4" /> {uploading ? "Subiendo…" : "Adjuntar reporte / captura"}<input type="file" className="hidden" disabled={uploading} onChange={(e) => uploadReport(e.target.files?.[0])} /></label>
                      {reportUrl && <a href={reportUrl} target="_blank" rel="noreferrer" className="block truncate text-xs font-medium text-emerald-700 hover:underline">{reportUrl}</a>}
                      <input data-testid={`mkt-report-url-${r.id}`} value={reportUrl} onChange={(e) => setReportUrl(e.target.value)} placeholder="o pega una URL del reporte (opcional)" className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-black" />
                    </>
                  )}
                  <textarea data-testid={`mkt-note-${r.id}`} value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder={acting.status === "rejected" ? "Motivo del rechazo (visible al artista)" : "Respuesta al artista (qué se hizo, resultados…)"} className="w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-black" />
                  <div className="flex gap-2">
                    <button data-testid={`mkt-confirm-${r.id}`} disabled={busy === r.id} onClick={() => decide(r.id, acting.status, { admin_note: note.trim(), report_url: reportUrl.trim() })} className="rounded-lg bg-black px-4 py-2 text-xs font-semibold text-white disabled:opacity-50">Confirmar</button>
                    <button onClick={() => { setActing(null); setNote(""); setReportUrl(""); }} className="rounded-lg border border-slate-300 px-4 py-2 text-xs font-medium hover:border-black">Cancelar</button>
                  </div>
                </div>
              ) : (
                <div className="mt-4 flex flex-wrap gap-2">
                  {r.status === "pending_verification" && <Act tid={`mkt-verify-${r.id}`} onClick={() => decide(r.id, "verified")} busy={busy === r.id} icon={Check} cls="border-blue-300 bg-blue-50 text-blue-700">Verificar pago</Act>}
                  {["verified", "pending_verification"].includes(r.status) && <Act tid={`mkt-run-${r.id}`} onClick={() => decide(r.id, "running")} busy={busy === r.id} icon={Play} cls="border-violet-300 bg-violet-50 text-violet-700">En curso</Act>}
                  {["verified", "running"].includes(r.status) && <Act tid={`mkt-complete-${r.id}`} onClick={() => { setActing({ id: r.id, status: "completed" }); setNote(""); setReportUrl(""); }} icon={Flag} cls="border-emerald-300 bg-emerald-50 text-emerald-700">Completar y responder</Act>}
                  {!["completed", "rejected"].includes(r.status) && <Act tid={`mkt-reject-${r.id}`} onClick={() => { setActing({ id: r.id, status: "rejected" }); setNote(""); }} icon={Ban} cls="border-red-300 bg-red-50 text-red-700">Rechazar</Act>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const Act = ({ tid, onClick, busy, icon: Icon, cls, children }) => (
  <button data-testid={tid} onClick={onClick} disabled={busy} className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold disabled:opacity-50 ${cls}`}><Icon className="h-3.5 w-3.5" /> {children}</button>
);
